/**
 * Reusable component for employer address with View Detail functionality
 * Matches the UI pattern from previous address section
 */

import { useState, useEffect, useRef, useId } from 'react'
import { AddressAutocomplete } from '@components/ui/AddressAutocomplete'
import { parseAddressComponents, extractAptUnit } from '@lib/utils/addressParser'
import type { PlaceResult } from '@/hooks/useGooglePlacesAutocomplete'
import { ADDRESS_CONSTANTS } from '@/constants/address'
import { useReferenceData } from '@/hooks/useReferenceData'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import { buildEmployerMapHref } from './employerMapHelpers'
import type {
  EmployerAddressDetailProps,
  EmployerAddressFields,
} from './EmployerAddressDetail.types'

export type { EmployerAddressFields } from './EmployerAddressDetail.types'

export function EmployerAddressDetail({
  value,
  onChange,
  onFieldsChange,
  onPlaceSelect,
  onAddressFieldBlur,
  placeholder = 'Enter address',
  label: _label = APPLICANT_CONTENT.application.sections.currentAddress.address,
  disabled = false,
  initialFields,
  fieldErrors = {},
  showValidation = false,
  propertyAddress,
}: EmployerAddressDetailProps) {
  const { states, isLoadingStates } = useReferenceData()
  const [showDetails, setShowDetails] = useState(false)
  const fieldIdSuffix = useId()
  const initFields: EmployerAddressFields = {
    address: value,
    streetName: initialFields?.streetName || '',
    aptUnit: initialFields?.aptUnit || '',
    city: initialFields?.city || '',
    state: initialFields?.state || '',
    zipCode: initialFields?.zipCode || '',
    country: initialFields?.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
    otherOutsideUS: initialFields?.otherOutsideUS || false,
  }
  const [addressFields, setAddressFields] = useState<EmployerAddressFields>(initFields)
  const addressFieldsRef = useRef<EmployerAddressFields>(initFields)

  // Sync external value and initial fields with internal state
  useEffect(() => {
    // Check if initial fields have any data that should be preserved.
    // This allows manual entry where 'value' (main address) might be empty but fields are keyed in.
    const hasInitialData = initialFields && (
      (initialFields.streetName && initialFields.streetName.trim() !== '') ||
      (initialFields.city && initialFields.city.trim() !== '') ||
      (initialFields.state && initialFields.state.trim() !== '') ||
      (initialFields.zipCode && initialFields.zipCode.trim() !== '')
    )

    // If value is cleared (empty), clear all address fields ONLY if no initial data provided
    if ((!value || value.trim() === '') && !hasInitialData) {
      setAddressFields(prev => {
        // Only clear if not already cleared to avoid unnecessary updates
        if (prev.address === '' && !prev.streetName && !prev.city) {
          return prev
        }
        const cleared: EmployerAddressFields = {
          address: '',
          streetName: '',
          aptUnit: '',
          city: '',
          state: '',
          zipCode: '',
          country: ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
          otherOutsideUS: false,
        }
        addressFieldsRef.current = cleared
        return cleared
      })
    } else {
      // Update fields from initialFields if provided, otherwise keep existing values
      setAddressFields(prev => {
        const updated = {
          ...prev,
          address: value,
          // Update fields from initialFields if provided
          ...(initialFields && {
            streetName: initialFields.streetName !== undefined ? initialFields.streetName : prev.streetName,
            aptUnit: initialFields.aptUnit !== undefined ? initialFields.aptUnit : prev.aptUnit,
            city: initialFields.city !== undefined ? initialFields.city : prev.city,
            state: initialFields.state !== undefined ? initialFields.state : prev.state,
            zipCode: initialFields.zipCode !== undefined ? initialFields.zipCode : prev.zipCode,
            country: initialFields.country !== undefined ? initialFields.country : prev.country,
            otherOutsideUS: initialFields.otherOutsideUS !== undefined ? initialFields.otherOutsideUS : prev.otherOutsideUS,
          }),
        }
        addressFieldsRef.current = updated
        return updated
      })
    }
  }, [value, initialFields])

  // Sync otherOutsideUS checkbox with country when country changes
  // But only if initialFields didn't explicitly set otherOutsideUS (preserve API state)
  useEffect(() => {
    // If initialFields explicitly set otherOutsideUS, preserve that value
    if (initialFields?.otherOutsideUS !== undefined) {
      if (addressFields.otherOutsideUS !== initialFields.otherOutsideUS) {
        setAddressFields(prev => {
          const updated = { ...prev, otherOutsideUS: initialFields.otherOutsideUS! }
          addressFieldsRef.current = updated
          return updated
        })
        if (onFieldsChange) {
          onFieldsChange({ otherOutsideUS: initialFields.otherOutsideUS })
        }
      }
      return // Don't auto-sync if initialFields provided the value
    }

    // Otherwise, auto-sync checkbox with country
    const countryCode = addressFields.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY
    const isOutsideUS = countryCode.toUpperCase() !== ADDRESS_CONSTANTS.DEFAULT_COUNTRY

    if (isOutsideUS !== addressFields.otherOutsideUS) {
      setAddressFields(prev => {
        const updated = { ...prev, otherOutsideUS: isOutsideUS }
        addressFieldsRef.current = updated
        return updated
      })
      if (onFieldsChange) {
        onFieldsChange({ otherOutsideUS: isOutsideUS })
      }
    }
  }, [addressFields.country, initialFields?.otherOutsideUS])

  const handlePlaceSelect = (place: PlaceResult) => {
    const parsed = parseAddressComponents(place)
    const formattedAddress = parsed.formattedAddress || ''

    // Update main address value
    onChange(formattedAddress)

    // Extract apartment/unit if present
    const apt = extractAptUnit(formattedAddress)

    // Street = street number + street name (match Angular: "2356 Sutter Street")
    const streetLine = [parsed.streetNumber, parsed.streetName].filter(Boolean).join(' ').trim() || (parsed.streetName || '')

    // Determine if outside US
    const countryCode = parsed.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY
    const isOutsideUS = Boolean(countryCode && countryCode.toUpperCase() !== ADDRESS_CONSTANTS.DEFAULT_COUNTRY)

    // Update address fields
    const newFields: EmployerAddressFields = {
      address: formattedAddress,
      streetName: streetLine,
      aptUnit: apt || '',
      city: parsed.city || '',
      state: parsed.state || '',
      zipCode: parsed.zipCode || '',
      country: countryCode,
      otherOutsideUS: isOutsideUS,
    }

    addressFieldsRef.current = newFields
    setAddressFields(newFields)

    // Notify parent component of field changes
    if (onFieldsChange) {
      onFieldsChange(newFields)
    }

    // Call optional place select callback (e.g., for distance calculation)
    if (onPlaceSelect) {
      onPlaceSelect(place)
    }
  }

  const handleFieldChange = (field: keyof EmployerAddressFields, fieldValue: string | boolean) => {
    const updatedFields = {
      ...addressFieldsRef.current,
      [field]: fieldValue,
    }
    addressFieldsRef.current = updatedFields
    setAddressFields(updatedFields)

    if (onFieldsChange) {
      onFieldsChange({ [field]: fieldValue })
    }
  }

  const handleZipCodeChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 6)
    handleFieldChange('zipCode', digitsOnly)
  }

  // Angular renders the Map link from the employer street address (autocomplete
  // value) and the employer city — mirror those exact inputs here.
  const mapHref = buildEmployerMapHref(propertyAddress, value, addressFields.city)

  return (
    <div className="w-full">
      <div className="flex gap-2 items-end">
        <div className="flex-1 max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">{_label}</label>
          <AddressAutocomplete
            value={value}
            onChange={onChange}
            onPlaceSelect={handlePlaceSelect}
            placeholder={placeholder}
            label=""
            disabled={disabled}
          />
        </div>
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          disabled={disabled}
          className={`px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {showDetails ? APPLICANT_CONTENT.application.buttons.closeDetail : APPLICANT_CONTENT.application.buttons.viewDetail}
        </button>
        {mapHref && (
          <a
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0 inline-flex items-center"
          >
            Map
          </a>
        )}
        <label className={`flex items-center px-4 py-2 whitespace-nowrap flex-shrink-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            id={`employer-outside-us-${fieldIdSuffix}`}
            checked={addressFields.otherOutsideUS}
            onChange={(e) => {
              if (!disabled) {
                handleFieldChange('otherOutsideUS', e.target.checked)
                if (e.target.checked) {
                  handleFieldChange('country', '')
                } else {
                  handleFieldChange('country', ADDRESS_CONSTANTS.DEFAULT_COUNTRY)
                }
              }
            }}
            disabled={disabled}
            className={`mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          />
          <span className="text-sm text-gray-700">{APPLICANT_CONTENT.application.sections.currentAddress.otherOutsideUS}</span>
        </label>
      </div>

      {showDetails && (
        <div className="mt-4 w-full md:-ml-[calc(50%+0.5rem)] md:w-[calc(150%+0.5rem)] md:pr-0">
          {/* Single row: Street* aligns with Company Name, then City*, State*, Zipcode*, Country* */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Street*</label>
              <input
                type="text"
                value={addressFields.streetName}
                onChange={(e) => handleFieldChange('streetName', e.target.value)}
                placeholder={APPLICANT_CONTENT.application.sections.property.street}
                disabled={disabled}
                className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${showValidation && fieldErrors.streetName ? 'border-red-500' : 'border-gray-300'
                  } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
              />
              {showValidation && fieldErrors.streetName && (
                <div className="mt-1 text-sm text-red-600">
                  {fieldErrors.streetName}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City*</label>
              <input
                type="text"
                value={addressFields.city}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                onBlur={() => onAddressFieldBlur?.(addressFieldsRef.current)}
                placeholder={APPLICANT_CONTENT.application.sections.currentAddress.city}
                disabled={disabled}
                className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${showValidation && fieldErrors.city ? 'border-red-500' : 'border-gray-300'
                  } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
              />
              {showValidation && fieldErrors.city && (
                <div className="mt-1 text-sm text-red-600">
                  {fieldErrors.city}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State*</label>
              {!addressFields.otherOutsideUS && states.length > 0 ? (
                <select
                  value={addressFields.state}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                  onBlur={() => onAddressFieldBlur?.(addressFieldsRef.current)}
                  disabled={disabled || isLoadingStates}
                  className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${showValidation && fieldErrors.state ? 'border-red-500' : 'border-gray-300'
                    } ${disabled || isLoadingStates ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
                >
                  <option value="">
                    {isLoadingStates ? 'Loading...' : 'Select State'}
                  </option>
                  {states.map((stateOption, index) => {
                    const optionValue = stateOption.state_code || stateOption.abbreviation || ''
                    const optionLabel = stateOption.state_name || stateOption.name || optionValue
                    return (
                      <option key={stateOption.state_code || `state-${index}`} value={optionValue}>
                        {optionLabel}
                      </option>
                    )
                  })}
                </select>
              ) : (
                <input
                  type="text"
                  value={addressFields.state}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                  onBlur={() => onAddressFieldBlur?.(addressFieldsRef.current)}
                  placeholder={addressFields.otherOutsideUS ? "State/Province" : "State"}
                  disabled={disabled}
                  className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${showValidation && fieldErrors.state ? 'border-red-500' : 'border-gray-300'
                    } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
                />
              )}
              {showValidation && fieldErrors.state && (
                <div className="mt-1 text-sm text-red-600">
                  {fieldErrors.state}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zipcode*</label>
              <input
                type="text"
                value={addressFields.zipCode}
                onChange={(e) => handleZipCodeChange(e.target.value)}
                onBlur={() => onAddressFieldBlur?.(addressFieldsRef.current)}
                placeholder={APPLICANT_CONTENT.application.sections.currentAddress.zipcode}
                maxLength={6}
                disabled={disabled}
                className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${showValidation && fieldErrors.zipCode ? 'border-red-500' : 'border-gray-300'
                  } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
              />
              {showValidation && fieldErrors.zipCode && (
                <div className="mt-1 text-sm text-red-600">
                  {fieldErrors.zipCode}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country*</label>
              <input
                type="text"
                value={addressFields.country}
                onChange={(e) => handleFieldChange('country', e.target.value)}
                placeholder={APPLICANT_CONTENT.application.sections.currentAddress.country}
                disabled={disabled}
                className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${showValidation && fieldErrors.country ? 'border-red-500' : 'border-gray-300'
                  } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
              />
              {showValidation && fieldErrors.country && (
                <div className="mt-1 text-sm text-red-600">
                  {fieldErrors.country}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
